const DRAFT_PREFIX = 'kedy-editor:draft:';

const getDefaultStorage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const createDraftPayload = (entry, state) => JSON.stringify({
  projectId: entry.projectId,
  manifest: entry.manifest,
  state,
  updatedAt: Date.now(),
}, (key, value) => {
  if (key === 'localThumbnail' || key === 'thumbnails') return undefined;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return undefined;
  return value;
});

export const readAutosaveDraft = (projectId, storage = getDefaultStorage()) => {
  if (!projectId || !storage) return null;
  try {
    const value = storage.getItem(`${DRAFT_PREFIX}${projectId}`);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

export class ProjectAutosaveQueue {
  constructor({ save, onStatus, delay = 650, storage = getDefaultStorage(), isOnline } = {}) {
    this.save = save;
    this.onStatus = onStatus || (() => {});
    this.delay = delay;
    this.storage = storage;
    this.isOnline = isOnline || (() => typeof navigator === 'undefined' || navigator.onLine !== false);
    this.pending = null;
    this.failed = null;
    this.timer = null;
    this.inFlight = null;
    this.handleOnline = () => this.retry();
    if (typeof window !== 'undefined') window.addEventListener('online', this.handleOnline);
  }

  persist(entry, state) {
    if (!entry?.projectId || !this.storage) return;
    try {
      this.storage.setItem(`${DRAFT_PREFIX}${entry.projectId}`, createDraftPayload(entry, state));
    } catch (error) {
      // Remote saving remains available when local storage is full or disabled.
    }
  }

  setStatus(state, detail = {}) {
    this.onStatus({ state, updatedAt: state === 'saved' ? Date.now() : null, ...detail });
  }

  enqueue(projectId, manifest, callbacks = {}) {
    if (!projectId || !manifest) return;
    this.pending = { projectId, manifest, callbacks };
    this.failed = null;
    this.persist(this.pending, 'pending');
    this.setStatus(this.isOnline() ? 'pending' : 'offline');
    this.schedule();
  }

  schedule(delay = this.delay) {
    clearTimeout(this.timer);
    if (!this.pending || this.inFlight || !this.isOnline()) return;
    this.timer = setTimeout(() => this.drain(), delay);
  }

  async drain() {
    if (this.inFlight || !this.pending) return this.inFlight;
    if (!this.isOnline()) {
      this.setStatus('offline');
      return null;
    }

    const entry = this.pending;
    this.pending = null;
    this.persist(entry, 'saving');
    this.setStatus('saving');

    this.inFlight = Promise.resolve()
      .then(() => this.save(entry.projectId, entry.manifest))
      .then((response) => {
        if (response?.state && response.state !== 'success') throw new Error('Autosave rejected');
        this.failed = null;
        this.persist(entry, 'saved');
        this.setStatus('saved');
        entry.callbacks.onSaved?.(response);
        return response;
      })
      .catch((error) => {
        this.failed = entry;
        this.persist(entry, this.isOnline() ? 'error' : 'offline');
        this.setStatus(this.isOnline() ? 'error' : 'offline', { message: error?.message });
        entry.callbacks.onError?.(error);
        return null;
      })
      .finally(() => {
        this.inFlight = null;
        if (this.pending) this.schedule(0);
      });

    return this.inFlight;
  }

  retry() {
    if (!this.pending && this.failed) {
      this.pending = this.failed;
      this.failed = null;
    }
    if (this.pending) {
      this.persist(this.pending, 'pending');
      this.setStatus(this.isOnline() ? 'pending' : 'offline');
      this.schedule(0);
    }
  }

  flush() {
    clearTimeout(this.timer);
    return this.drain();
  }

  destroy() {
    clearTimeout(this.timer);
    if (typeof window !== 'undefined') window.removeEventListener('online', this.handleOnline);
  }
}
